import { useCallback, useEffect, useState } from 'react'
import { errorMessage, getUser, listJobRuns, listSettings, settleRewards } from '../../services/admin'
import { faDigits } from '../../lib/numerals'
import { formatDatetime } from './dates'
import {
  BADGE_FA,
  CATEGORY_FA,
  PERIOD_TYPES,
  RUN_STATUS_FA,
  describeDisabled,
  describeSettleOutcome,
  isSettleKey,
  keyPlaceholder,
  settleJobKey,
  settleLabel,
  settleRefusal,
  settleSummaryOf,
  shiftSettleKey,
  shortId,
  switchesOff,
} from './rewards'

// ─── Rewards tab (admin-rewards, V6-p) ────────────────────────────────────────

const inputCls = 'rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none dark:bg-slate-800'
const btnGhost = 'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-ink-2 hover:bg-slate-50 disabled:opacity-40'
const card = 'rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm dark:bg-slate-800'
const errBox = 'rounded-lg bg-bad-tint px-4 py-3 text-sm text-bad-ink'
const TONE_CLS = {
  ok: 'bg-good-tint text-good-ink',
  info: 'bg-brand-50 text-brand-700',
  warn: 'bg-warn-tint text-warn-ink',
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg bg-slate-50 px-3 py-2 dark:bg-surface-2">
      <div className="text-xs text-ink-3">{label}</div>
      <div className="text-base font-semibold text-ink">{faDigits(value ?? 0)}</div>
    </div>
  )
}

/**
 * One settlement summary (`SettleSummary`, V6-b-settle): the prize table in
 * force, the totals and every award — rank, board, winner, gems, badge, and
 * for a real run whether this run booked it. `names` maps a user id to a
 * display name when the tab could read it.
 */
export function SettleSummaryView({ summary, names = {}, real = false }) {
  const awards = summary.awards ?? []
  const prizes = summary.prizeGems ?? {}
  return (
    <div className="space-y-3">
      <div className="text-sm font-medium text-ink">{settleLabel(summary.periodType, summary.periodKey)}
        <span className="ms-2 font-mono text-xs text-ink-3" dir="ltr">{summary.periodKey}</span>
      </div>
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Stat label="برندگان" value={summary.totals?.winners} />
        <Stat label="جم" value={summary.totals?.gems} />
        <Stat label="نشان" value={summary.totals?.badges} />
        <Stat label="اعلان فرستاده‌شده" value={summary.notified} />
      </div>
      <p className="text-xs text-ink-2">
        جوایز جدول XP: {prizes.xp?.length ? prizes.xp.map(n => faDigits(n)).join('، ') : 'هیچ'} جم
        {summary.periodType === 'week' && <> · هر عضو تیم برتر: {prizes.teams?.length ? prizes.teams.map(n => faDigits(n)).join('، ') : 'هیچ'} جم</>}
      </p>
      {summary.disabled?.length > 0 && (
        <div className="rounded-lg bg-warn-tint px-3 py-2 text-xs leading-6 text-warn-ink">
          چرا پرداخت نشد: {describeDisabled(summary.disabled).join(' · ')}
        </div>
      )}
      {awards.length === 0
        ? <p className="py-4 text-center text-sm text-ink-3">در این دوره کسی XP نگرفته است؛ برنده‌ای نیست و چیزی پرداخت نمی‌شود.</p>
        : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs text-ink-3">
                <tr>
                  <th className="px-2 py-1.5 text-start font-medium">رتبه</th>
                  <th className="px-2 py-1.5 text-start font-medium">جدول</th>
                  <th className="px-2 py-1.5 text-start font-medium">برنده</th>
                  <th className="px-2 py-1.5 text-start font-medium">جم</th>
                  <th className="px-2 py-1.5 text-start font-medium">نشان</th>
                  {real && <th className="px-2 py-1.5 text-start font-medium">در این اجرا</th>}
                </tr>
              </thead>
              <tbody>
                {awards.map(award => (
                  <tr key={`${award.category}:${award.rank}:${award.userId}`} className="border-t border-line">
                    <td className="px-2 py-1.5 text-ink">{faDigits(award.rank)}</td>
                    <td className="px-2 py-1.5 text-ink-2">
                      {CATEGORY_FA[award.category] ?? award.category}
                      {award.teamId && <span className="ms-1 font-mono text-xs text-ink-3" dir="ltr">{shortId(award.teamId)}</span>}
                    </td>
                    <td className="px-2 py-1.5 text-ink">
                      {names[award.userId] ?? <span className="text-ink-3">بی‌نام</span>}
                      <span className="ms-1 font-mono text-xs text-ink-3" dir="ltr">{shortId(award.userId)}</span>
                    </td>
                    <td className="px-2 py-1.5 text-ink">{faDigits(award.gems)}</td>
                    <td className="px-2 py-1.5 text-ink-2">{award.badgeKey ? (BADGE_FA[award.badgeKey] ?? award.badgeKey) : '—'}</td>
                    {real && (
                      <td className="px-2 py-1.5 text-xs text-ink-2">
                        {award.paid ? 'جم ثبت شد' : 'جم از قبل/بدون جم'}{award.badgeKey ? (award.badgeAwarded ? ' · نشان داده شد' : ' · نشان از قبل') : ''}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {summary.errors?.length > 0 && (
        <div className="rounded-lg bg-bad-tint px-3 py-2 text-xs text-bad-ink" dir="auto">
          خطای اعلان (پرداخت سر جایش است): {summary.errors.join(' · ')}
        </div>
      )}
    </div>
  )
}

/**
 * «اجرای خشک تسویه» and, only after it, the real run — the V4-p reshuffle
 * pattern. The real button does not exist until a dry run of this very
 * period has been shown, and it asks for the period key typed back. With
 * the switches off (`off` non-empty) the real run is not offered at all:
 * the server would answer `idle` and write nothing anyway, and the owner
 * decides when they go on.
 */
export function SettlePanel({ periodType, periodKey, off, onRan, initialResult = null }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [result, setResult] = useState(initialResult)
  const [confirming, setConfirming] = useState(false)
  const [typed, setTyped] = useState('')
  const [names, setNames] = useState({})

  useEffect(() => { setResult(initialResult); setConfirming(false); setTyped(''); setErr('') }, [periodType, periodKey, initialResult])

  const summary = settleSummaryOf(result)
  const outcome = result ? describeSettleOutcome(result) : null
  // The dry run must be of exactly the period on screen — an empty key means "the last closed", which the answer names.
  const dryKey = result?.outcome === 'dry-run' ? summary?.periodKey : null
  const dryDone = Boolean(dryKey) && (!periodKey || dryKey === periodKey) && summary?.periodType === periodType
  const switchesKnown = Array.isArray(off)
  const canPay = switchesKnown && off.length === 0

  // Winners are ids on the wire; read their display names once per summary (a handful of learners).
  useEffect(() => {
    const ids = [...new Set((summary?.awards ?? []).map(award => award.userId))].slice(0, 40)
    if (ids.length === 0) return undefined
    let alive = true
    Promise.all(ids.map(id => getUser(id).then(user => [id, user?.displayName ?? null]).catch(() => [id, null])))
      .then(pairs => { if (alive) setNames(Object.fromEntries(pairs.filter(([, name]) => name))) })
    return () => { alive = false }
  }, [summary])

  async function run(dryRun) {
    setBusy(true); setErr('')
    try {
      const answer = await settleRewards({ periodType, periodKey: dryRun ? periodKey || undefined : dryKey, dryRun })
      setResult(answer)
      setConfirming(false); setTyped('')
      if (!dryRun) onRan(answer)
    } catch (ex) {
      setErr(settleRefusal(ex) ?? errorMessage(ex))
    } finally { setBusy(false) }
  }

  const real = result?.outcome === 'ran' || result?.outcome === 'already-done'

  return (
    <section className={`${card} space-y-3`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-ink">
          تسویهٔ {periodType === 'week' ? 'هفته' : 'ماه'} — {periodKey ? settleLabel(periodType, periodKey) : 'آخرین دورهٔ بسته‌شده'}
        </h3>
        <button type="button" disabled={busy || (periodKey && !isSettleKey(periodType, periodKey))} onClick={() => run(true)} className={`ms-auto ${btnGhost}`}>
          {busy && !confirming ? 'در حال اجرا…' : 'اجرای خشک تسویه'}
        </button>
      </div>
      <p className="text-xs leading-6 text-ink-2">
        اجرای خشک نشان می‌دهد در این دوره چه کسانی چند جم و کدام نشان می‌گیرند و هیچ چیزی نمی‌نویسد (دورهٔ در جریان هم پیش‌نمایش می‌شود).
        اجرای واقعی فقط پس از اجرای خشک همین دوره، و فقط وقتی کلیدهای پرداخت روشن‌اند، در دسترس است.
      </p>
      {switchesKnown && off.length > 0 && (
        <div className="rounded-lg bg-warn-tint px-3 py-2 text-xs leading-6 text-warn-ink">
          پرداخت واقعی خاموش است — {describeDisabled(off).join(' و ')}. روشن‌کردن این کلیدها تصمیم مالک است (تب تنظیمات).
          تا آن زمان فقط اجرای خشک ممکن است و هیچ دوره‌ای از دست نمی‌رود.
        </div>
      )}
      {!switchesKnown && (
        <p className="text-xs text-ink-3">وضعیت کلیدهای پرداخت خوانده نشد؛ تا خوانده نشود فقط اجرای خشک پیشنهاد می‌شود.</p>
      )}
      {err && <div className={errBox}>{err}</div>}
      {outcome && <div className={`rounded-lg px-3 py-2 text-sm ${TONE_CLS[outcome.tone]}`}>{outcome.text}</div>}
      {summary && <SettleSummaryView summary={summary} names={names} real={real} />}

      {dryDone && canPay && !confirming && (
        <button type="button" disabled={busy} onClick={() => setConfirming(true)} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
          پرداخت واقعی جوایز…
        </button>
      )}
      {dryDone && canPay && confirming && (
        <div className="space-y-2 rounded-lg bg-bad-tint px-3 py-3 text-sm text-bad-ink">
          <p>
            تسویهٔ واقعی به برندگان بالا جم و نشان می‌دهد و اعلان می‌فرستد و از پنل برگشت‌پذیر نیست (هر دوره فقط یک بار پرداخت می‌شود).
            برای تأیید، کلید دوره (<span dir="ltr" className="font-mono">{dryKey}</span>) را بنویسید.
          </p>
          <input type="text" value={typed} onChange={e => setTyped(e.target.value)} dir="ltr" placeholder={dryKey} className={`w-40 ${inputCls}`} aria-label="کلید دوره برای تأیید" autoComplete="off" />
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={busy || typed.trim() !== dryKey} onClick={() => run(false)}
              className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
              {busy ? 'در حال اجرا…' : 'بله، جوایز را بپرداز'}
            </button>
            <button type="button" disabled={busy} onClick={() => { setConfirming(false); setTyped('') }} className={btnGhost}>انصراف</button>
          </div>
        </div>
      )}
    </section>
  )
}

/** The settlement runs of one period type (`admin-jobs_list`), newest first; a row opens its summary. */
export function SettleHistory({ periodType, runs, openId, onOpen }) {
  if (runs.length === 0) {
    return <p className="py-6 text-center text-sm text-ink-3">{`هنوز هیچ تسویهٔ ${periodType === 'week' ? 'هفتگی' : 'ماهانه'} اجرا نشده است.`}</p>
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-xs text-ink-3">
          <tr>
            <th className="px-2 py-1.5 text-start font-medium">دوره</th>
            <th className="px-2 py-1.5 text-start font-medium">وضعیت</th>
            <th className="px-2 py-1.5 text-start font-medium">شروع</th>
            <th className="px-2 py-1.5 text-start font-medium">برندگان</th>
            <th className="px-2 py-1.5 text-start font-medium">جم</th>
            <th className="px-2 py-1.5" />
          </tr>
        </thead>
        <tbody>
          {runs.map(run => (
            <tr key={run.id} className={`border-t border-line ${run.id === openId ? 'bg-brand-50' : ''}`}>
              <td className="px-2 py-1.5 text-ink">
                {settleLabel(periodType, run.periodKey)}
                <span className="ms-1 font-mono text-xs text-ink-3" dir="ltr">{run.periodKey}</span>
              </td>
              <td className={`px-2 py-1.5 ${run.status === 'failed' ? 'text-bad-ink' : 'text-ink-2'}`}>{RUN_STATUS_FA[run.status] ?? run.status}</td>
              <td className="px-2 py-1.5 text-xs text-ink-2">{formatDatetime(run.startedAt)}</td>
              <td className="px-2 py-1.5 text-ink-2">{faDigits(run.summary?.totals?.winners ?? 0)}</td>
              <td className="px-2 py-1.5 text-ink-2">{faDigits(run.summary?.totals?.gems ?? 0)}</td>
              <td className="px-2 py-1.5 text-end">
                <button type="button" onClick={() => onOpen(run.id)} className={btnGhost}>{run.id === openId ? 'بستن' : 'گزارش'}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** A past run opened from the history: its summary, or the error a failed run stored. */
function RunReport({ run }) {
  if (run.status === 'failed' || !run.summary?.awards) {
    return (
      <div className={errBox} dir="auto">
        {run.status === 'failed' ? 'این اجرا ناموفق بود' : 'گزارشی ثبت نشده'}{run.summary?.error ? `: ${run.summary.error}` : '.'}
      </div>
    )
  }
  return <SettleSummaryView summary={run.summary} real />
}

/**
 * The «جوایز» tab (V6-p): pick week or month and a period, dry-run the
 * settlement to see who would win what, pay for real only after that and a
 * typed confirmation, and read every past settlement from `admin-jobs_list`.
 * Nothing here turns a switch on; the prize amounts are edited in the
 * settings tab («جوایز و تسویهٔ دوره»).
 */
export function RewardsTab() {
  const [periodType, setPeriodType] = useState('week')
  const [periodKey, setPeriodKey] = useState('') // '' = the server's last closed period
  const [periodInput, setPeriodInput] = useState('')
  const [settings, setSettings] = useState(null)
  const [runs, setRuns] = useState(null)
  const [runsErr, setRunsErr] = useState('')
  const [err, setErr] = useState('')
  const [openRun, setOpenRun] = useState(null)

  const loadRuns = useCallback(async () => {
    setRunsErr('')
    try {
      const answer = await listJobRuns(settleJobKey(periodType))
      setRuns(answer.items ?? [])
    } catch (ex) { setRunsErr(errorMessage(ex)); setRuns([]) }
  }, [periodType])

  useEffect(() => { setRuns(null); setOpenRun(null); loadRuns() }, [loadRuns])
  useEffect(() => { listSettings().then(setSettings).catch(() => setSettings(null)) }, [])

  const off = switchesOff(settings)

  function goTo(type, key) {
    setErr('')
    setPeriodType(type)
    setPeriodKey(key)
    setPeriodInput(key)
  }

  function applyInput(e) {
    e.preventDefault()
    const key = periodInput.trim()
    if (key === '') { goTo(periodType, ''); return }
    if (!isSettleKey(periodType, key)) {
      setErr(periodType === 'week'
        ? 'هفته باید به شکل W + تاریخ میلادی شنبهٔ آن باشد، مثلاً W2026-09-19.'
        : 'ماه باید به شکل YYYY-MM باشد، مثلاً 1405-06.')
      return
    }
    goTo(periodType, key)
  }

  const shown = runs?.find(run => run.id === openRun) ?? null

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-ink-2">نوع دوره</span>
        {PERIOD_TYPES.map(type => (
          <button key={type.id} type="button" onClick={() => goTo(type.id, '')}
            className={`rounded-lg px-3 py-1.5 text-sm ${type.id === periodType ? 'bg-brand-50 font-medium text-brand-700' : 'text-ink-3 hover:bg-slate-100'}`}>
            {type.label}
          </button>
        ))}
        <span className="ms-4 text-sm text-ink-2">دوره</span>
        <button type="button" disabled={!isSettleKey(periodType, periodKey)} onClick={() => goTo(periodType, shiftSettleKey(periodType, periodKey, -1))} className={btnGhost} aria-label="دورهٔ قبل">‹</button>
        <form onSubmit={applyInput} className="flex items-center gap-1">
          <input type="text" value={periodInput} onChange={e => setPeriodInput(e.target.value)} dir="ltr" placeholder={keyPlaceholder(periodType)} className={`w-36 ${inputCls}`} aria-label="کلید دوره" />
          <button type="submit" className={btnGhost}>برو</button>
        </form>
        <button type="button" disabled={!isSettleKey(periodType, periodKey)} onClick={() => goTo(periodType, shiftSettleKey(periodType, periodKey, 1))} className={btnGhost} aria-label="دورهٔ بعد">›</button>
        {periodKey && <button type="button" onClick={() => goTo(periodType, '')} className="text-xs text-brand-600 hover:underline">آخرین دورهٔ بسته‌شده</button>}
      </div>
      <p className="text-xs text-ink-3">خالی = آخرین {periodType === 'week' ? 'هفتهٔ' : 'ماهِ'} بسته‌شده (به انتخاب سرور). هفته از شنبه تا جمعه به وقت تهران است.</p>
      {err && <div className={errBox}>{err}</div>}

      <SettlePanel
        periodType={periodType}
        periodKey={periodKey}
        off={off}
        onRan={() => loadRuns()}
      />

      <section className={`${card} space-y-3`}>
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-ink">تاریخچهٔ تسویه‌های {periodType === 'week' ? 'هفتگی' : 'ماهانه'}</h3>
          <button type="button" onClick={loadRuns} className={`ms-auto ${btnGhost}`}>تازه‌سازی</button>
        </div>
        {runsErr && <div className={errBox}>{runsErr}</div>}
        {runs === null
          ? <p className="py-6 text-center text-sm text-ink-3">در حال بارگذاری…</p>
          : <SettleHistory periodType={periodType} runs={runs} openId={openRun} onOpen={id => setOpenRun(openRun === id ? null : id)} />}
        {shown && <RunReport run={shown} />}
      </section>
    </div>
  )
}
